import React, { useEffect, useState } from "react";
import {
  AlertCircle,
  ArrowRight,
  User,
  Phone,
  Copy,
  FileJson,
  Info,
  Loader,
  Loader2,
  CheckCircle,
  Landmark,
  QrCode,
  CreditCard,
  Hash,
  MapPin,
} from "lucide-react";
import { useService } from "../../../context/ServiceContext";
import { showErrorToast, showSuccessToast } from "../../../utils/toastUtils";
import { getMobileToUpiAdvanceSrv2Details } from "../../../Services/ApiTestService/apiTestService";
import { fetchProfile } from "../../../Services/profile/profileservice";
import Seo from "../../../Components/SeoComponent/Seo";
import Header from "../../../Components/Header";

// IMPORTANT: Update this import to match your actual service function for MobileToUpiAdvanceSrv2

function MobileToUpiAdvanceSrv2() {
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState(null);

  const [mobileNumber, setMobileNumber] = useState("");
  const [inputError, setInputError] = useState(null);
  const [isJsonCopied, setIsJsonCopied] = useState(null);
  const { serviceName } = useService();

  const [profileData, setProfileData] = useState(null);

  // Basic Validation Checker
  const isValidMobile = (val) => /^[0-9]{10}$/.test(val);

  const handleCopy = (data) => {
    navigator.clipboard
      .writeText(JSON.stringify(data, null, 2))
      .then(() => showSuccessToast("Copied!"));

    setIsJsonCopied(true);
    setTimeout(() => setIsJsonCopied(false), 2000);
  };

  const handleFetchData = async () => {
    if (!profileData) return;

    // Validation
    if (!mobileNumber) {
      return setInputError("Mobile number is required.");
    }

    if (!isValidMobile(mobileNumber)) {
      return setInputError("⚠️ Please enter a valid 10-digit Mobile Number.");
    }

    try {
      setInputError(null);
      setIsLoading(true);
      setResponse(null);

      const res = await getMobileToUpiAdvanceSrv2Details({
        api_id: profileData.user_id,
        api_key: profileData.api_key,
        token_id: profileData.token_id,
        mobile: mobileNumber.trim(),
      });

      setResponse(res);

      if (res?.status?.code === 200) {
        showSuccessToast(res?.message || "Success");
      } else {
        showErrorToast(
          res?.data?.message ||
            res?.message ||
            res?.data?.data?.message ||
            res?.data?.error?.message ||
            res?.status?.message ||
            "Something went wrong"
        );
      }
    } catch (error) {
      console.error(error);
      showErrorToast("Server error");
    } finally {
      setIsLoading(false);
    }
  };

  // ---------------- FETCH PROFILE ----------------
  useEffect(() => {
    const loadProfile = async () => {
      try {
        setIsLoading(true);
        const res = await fetchProfile();
        setProfileData(res?.data || null);
      } catch (error) {
        console.error(error);
        showErrorToast("Failed to fetch profile");
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, []);

  return (
    <>
      <Seo />
      <Header heading={serviceName || "Mobile to UPI Advance Srv2"} />

      {isLoading && (
        <div className="flex justify-center mt-6">
          <Loader className="animate-spin text-blue-600" />
        </div>
      )}

      {/* Mesh Gradient Background Wrapper */}
      <div className="w-full min-h-screen flex flex-col items-center justify-start pb-14 bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-blue-50 via-slate-50 to-indigo-50/30">
        
        {/* ---------------- FORM (Glassmorphism UI) ---------------- */}
        <div className="max-w-4xl w-full mt-2 rounded-3xl border border-white/60 bg-white/40 backdrop-blur-2xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          {/* Header */}
          <div className="flex items-center gap-4 border-b border-slate-200/60 pb-6">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-lg shadow-blue-500/30">
              <User size={28} />
            </div>

            <div>
              <h2 className="text-2xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
               {serviceName || "Mobile to UPI Advance V2"}
              </h2>
              <p className="text-sm text-slate-500 mt-1">
                Retrieve advanced UPI details using a Mobile Number.
              </p>
            </div>
          </div>

          <div className="flex justify-center w-full ">
                {/* Input and button */}
          <div className="mt-2 relative z-10 w-full justify-center md:flex items-end gap-6 md:max-w-md">
            {/* Mobile Number */}
            <div className="group w-full flex-1">
              <label className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700">
                <Phone size={17} className="text-indigo-500" />
                Mobile Number
              </label>
              <input
                type="tel"
                maxLength={10}
                value={mobileNumber}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "");
                  setMobileNumber(val);
                }}
                placeholder="Eg: 9876543210"
                className="w-full rounded-xl border border-black/20 bg-white/50 px-4 py-3.5 text-sm transition-all duration-300 placeholder:text-slate-400 focus:bg-white focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 outline-none shadow-sm"
              />
            </div>

               {/* Button */}
          <div className="mt-8 flex justify-end">
            <button
              disabled={isLoading || !mobileNumber}
              onClick={handleFetchData}
              className="group relative inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-3.5 font-semibold text-white shadow-[0_8px_20px_-6px_rgba(79,70,229,0.5)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_25px_-6px_rgba(79,70,229,0.6)] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0"
            >
              {isLoading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Fetching...
                </>
              ) : (
                <>
                  Fetch Details
                  <ArrowRight
                    size={18}
                    className="transition-transform duration-300 group-hover:translate-x-1"
                  />
                </>
              )}
            </button>
          </div>
          </div>
          </div>

      

          {/* Error */}
          {inputError && (
            <div className="mt-6 flex items-center gap-3 rounded-xl border border-red-200/80 bg-red-50/80 backdrop-blur-sm px-5 py-4 text-red-700 shadow-sm animate-in fade-in slide-in-from-top-2">
              <AlertCircle size={18} />
              <span className="text-sm font-semibold">{inputError}</span>
            </div>
          )}

       
        </div>

        {/* Response Message */}
        {response && (
          <div
            className={`mt-6 ${
              response.status?.code !== 200 ? "text-red-500" : "text-green-600"
            } py-2 text-xl font-bold animate-in fade-in`}
          >
            {response?.data?.message || response?.message}
          </div>
        )}

        {/* Instructions */}
        <div className="w-full flex justify-center px-4">
          <div className="bg-blue-50/80 backdrop-blur-md max-w-3xl w-full border border-blue-200/60 text-blue-800 p-5 mt-8 rounded-2xl shadow-sm">
            <h3 className="font-bold flex items-center justify-start gap-2 text-lg mb-2">
              <Info size={20} /> Important Instructions{" "}
              <span className="text-red-500">*</span>
            </h3>
            <p className="text-sm leading-relaxed opacity-90">
              Please enter a valid 10-digit Mobile Number. After clicking the
              fetch button, the system will return the structured UPI advanced
              data associated with the provided number.
            </p>
          </div>
        </div>

        {/* ---------------- RESULT ---------------- */}
        {response?.status?.code === 200 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10 w-full max-w-6xl px-4 animate-in slide-in-from-bottom-4 duration-500">
            {/* -------- JSON VIEW -------- */}
            <div className="bg-slate-900/95 relative text-emerald-300 p-6 rounded-2xl overflow-auto shadow-2xl border border-slate-700">
              <button
                onClick={() => handleCopy(response)}
                className="absolute text-slate-300 hover:text-white top-5 right-6 transition-colors bg-white/10 p-2 rounded-lg backdrop-blur-sm"
              >
                {isJsonCopied ? (
                  <span className="text-xs font-bold text-emerald-400">
                    Copied!
                  </span>
                ) : (
                  <Copy size={18} />
                )}
              </button>
              <p className="font-bold mb-4 text-xl flex items-center justify-start gap-2 text-white">
                <FileJson className="text-indigo-400" /> JSON Response
              </p>
              <hr className="mb-6 border-slate-700" />
              <pre className="text-[14px] font-mono leading-relaxed">
                {JSON.stringify(response, null, 2)}
              </pre>
            </div>

            {/* -------- PARSED DATA VIEW -------- */}
            {(() => {
              const data = response?.data?.data || {};
              
              // Array to easily map through the details
              const details = [
                { label: "Account Name", value: data.name, icon: User, color: "text-blue-600", bg: "bg-blue-100" },
                { label: "UPI ID", value: data.upi_id, icon: QrCode, color: "text-purple-600", bg: "bg-purple-100" },
                { label: "PSP Name", value: data.psp_name, icon: CreditCard, color: "text-pink-600", bg: "bg-pink-100" },
                { label: "Bank Branch", value: data.branch_name, icon: Landmark, color: "text-amber-600", bg: "bg-amber-100" },
                { label: "IFSC Code", value: data.ifsc_code, icon: Hash, color: "text-emerald-600", bg: "bg-emerald-100" },
                { label: "Branch Address", value: data.branch_address, icon: MapPin, color: "text-indigo-600", bg: "bg-indigo-100" },
              ];

              return (
                <div className="bg-white/90 backdrop-blur-xl p-6 md:p-8 rounded-2xl border border-white/60 shadow-xl relative overflow-hidden">
                  {/* Decorative background icon */}
                  <div className="absolute -top-6 -right-6 text-slate-50 opacity-[0.03] rotate-12 pointer-events-none">
                    <Landmark size={180} />
                  </div>

                  {/* Header */}
                  <div className="flex items-center gap-3 mb-6 border-b border-slate-200/80 pb-5 relative z-10">
                    <div className="bg-green-500 text-white p-2 rounded-xl shadow-md shadow-green-500/20">
                      <CheckCircle size={22} strokeWidth={2.5} />
                    </div>
                    <div>
                      <h3 className="text-xl font-extrabold text-slate-800">Advanced UPI Details</h3>
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest mt-1">Verified Information</p>
                    </div>
                  </div>

                  {/* Content List */}
                  <div className="space-y-5 relative z-10">
                    {details.map((detail, idx) => 
                      detail.value ? (
                        <div key={idx} className="flex items-start gap-4 group">
                          <div className={`mt-0.5 flex-shrink-0 p-2.5 rounded-xl ${detail.bg} ${detail.color} shadow-sm transition-transform duration-300 group-hover:scale-110`}>
                            <detail.icon size={18} strokeWidth={2.5} />
                          </div>
                          <div className="flex-1">
                            <p className="text-[12px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">
                              {detail.label}
                            </p>
                            <p className="text-[15px] font-semibold text-slate-800 leading-snug">
                              {detail.value}
                            </p>
                          </div>
                        </div>
                      ) : null
                    )}
                  </div>
                </div>
              );
            })()} </div>
        )}
      </div>
    </>
  );
}

export default MobileToUpiAdvanceSrv2;